import { useState, useCallback } from 'react';
import { useMutation, useQuery, useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PiracyScanner } from '../scanner';
import type { ContentInput } from '../lib/types';
import { retryWithBackoff, categorizeError } from '../lib/retry';
import { phaseFromMessage } from '../lib/scanPhase';

export function useScanner() {
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [progressCount, setProgressCount] = useState<{ current: number; total: number } | null>(null);

  const convex = useConvex();
  const createViolation = useMutation(api.violations.createViolation);
  const updateScanStatus = useMutation(api.scans.updateScanStatus);
  const updateScanProgress = useMutation(api.scans.updateScanProgress);
  const updateVisibilityScores = useMutation(api.scans.updateVisibilityScores);
  const createVisibilityData = useMutation(api.searchVisibility.createVisibilityData);
  const updateHostingInfo = useMutation(api.violations.updateHostingInfo);
  const updateDomainAnalytics = useMutation(api.domainAnalytics.updateDomainAnalytics);
  const updateRetryStats = useMutation(api.scans.updateRetryStats);
  const whitelist = useQuery(api.whitelist.list);
  const visibilityConfig = useQuery(api.settings.getVisibilityScorerConfig);

  const runScan = useCallback(async (
    scanId: any,
    content: ContentInput
  ) => {
    setIsScanning(true);
    setProgress(`🚀 Starting scan for ${content.title}...`);

    try {
      // Update status to scanning
      await updateScanStatus({
        scanId,
        status: 'scanning',
      });

      setProgress(`🔧 Initializing scanner with API keys...`);

      // Get whitelist domains
      const whitelistDomains = whitelist?.map(entry => entry.domain) || [];

      // Prime UI with an initial phase to avoid a blank state before first progress event
      // Also enforce monotonic phase progression so UI phases don't jump backwards
      const orderMap: Record<string, number> = {
        searching: 1,
        scraping: 2,
        classifying: 3,
        hostingLookup: 4,
        visibility: 5,
        finalizing: 6,
      } as const;
      let lastPhaseOrder = 0;
      const setPhase = async (
        phase: string,
        progressText?: string,
        progressCurrent?: number,
        progressTotal?: number
      ) => {
        const next = orderMap[phase] || 0;
        if (next < lastPhaseOrder) {
          return; // ignore out-of-order updates
        }
        lastPhaseOrder = next;
        try {
          await updateScanProgress({
            scanId,
            currentPhase: phase as any,
            progressText,
            progressCurrent,
            progressTotal,
          });
        } catch {}
      };
      try {
        await setPhase('searching', 'Preparing search queries…', 0, 0);
      } catch {}

      // Initialize scanner. All external API keys are used on Convex only.
      // We intentionally pass empty strings for keys here to avoid any client exposure.
      // Simple background retry queue with counters persisted to scan row
      const retryCounts = { exa: 0, firecrawl: 0, brave: 0 } as Record<'exa'|'firecrawl'|'brave', number>;
      let retryWorking = false;
      type RetryTask = { engine: 'exa'|'firecrawl'|'brave'; run: () => Promise<void> };
      const queue: RetryTask[] = [];
      // helper to push retry counts into the scan row subtitle
      const pushCounts = async () => {
        try {
          await updateScanProgress({ scanId, progressText: `${progress} · Retries: exa ${retryCounts.exa}, firecrawl ${retryCounts.firecrawl}, brave ${retryCounts.brave}` });
        } catch {}
      };

      const scheduleWorker = () => {
        if (retryWorking) return;
        retryWorking = true;
        (async function work() {
          while (queue.length > 0) {
            const task = queue.shift()!;
            try {
              await task.run();
              retryCounts[task.engine] = Math.max(0, retryCounts[task.engine] - 1);
              await updateRetryStats({ scanId, retryExa: retryCounts.exa, retryFirecrawl: retryCounts.firecrawl, retryBrave: retryCounts.brave });
            } catch {
              // leave it decremented; we won't re-enqueue infinitely
            }
            await new Promise(r => setTimeout(r, 1000));
          }
          retryWorking = false;
        })();
      };

      // Aggregate search progress across engines so UI shows monotonic progress
      const searchTotals = {
        exa: { current: 0, total: 0 },
        firecrawl: { current: 0, total: 0 },
        brave: { current: 0, total: 0 },
      } as Record<'exa'|'firecrawl'|'brave', { current: number; total: number }> & any;

      const updateAggregatedSearch = () => {
        const current = searchTotals.exa.current + searchTotals.firecrawl.current + searchTotals.brave.current;
        const total = (searchTotals.exa.total || 0) + (searchTotals.firecrawl.total || 0) + (searchTotals.brave.total || 0);
        if (total > 0) {
          setPhase('searching', `Searching ${current}/${total}`, current, total).catch(()=>{});
        }
      };

      const scanner = new PiracyScanner({
        exaApiKey: '',
        firecrawlApiKey: '',
        screenshotApiKey: '',
        openRouterApiKey: '',
        convexClient: convex,
        whitelistDomains,
        // Visibility scoring configuration
        osnDomains: visibilityConfig?.osnDomains,
        visibilityAlpha: visibilityConfig?.alpha,
        visibilityBeta: visibilityConfig?.beta,
        onProgress: (msg) => {
          console.log(`[Scanner] ${msg}`);
          setProgress(msg);
          const phase = phaseFromMessage(msg);
          setPhase(phase, msg).catch(()=>{});
        },
        onProgressWithCount: (msg, current, total) => {
          console.log(`[Scanner] ${msg} (${current}/${total})`);
          setProgress(msg);
          setProgressCount({ current, total });
          const phase = phaseFromMessage(msg);
          // Aggregate the three search sub-steps into a single monotonic progress bar
          const lower = msg.toLowerCase();
          const isSearching = phase === 'searching' && lower.includes('search');
          if (isSearching) {
            if (lower.includes('exa')) {
              searchTotals.exa.current = current; searchTotals.exa.total = total;
              updateAggregatedSearch();
              return;
            }
            if (lower.includes('firecrawl')) {
              searchTotals.firecrawl.current = current; searchTotals.firecrawl.total = total;
              updateAggregatedSearch();
              return;
            }
            if (lower.includes('brave')) {
              searchTotals.brave.current = current; searchTotals.brave.total = total;
              updateAggregatedSearch();
              return;
            }
          }

          setPhase(phase, msg, current, total).catch(()=>{});
        },
        onEnqueueRetry: async (engine, run) => {
          queue.push({ engine, run });
          retryCounts[engine]++;
          try { await updateRetryStats({ scanId, retryExa: retryCounts.exa, retryFirecrawl: retryCounts.firecrawl, retryBrave: retryCounts.brave }); } catch {}
          scheduleWorker();
        },
      });

      setProgress(`✅ Scanner initialized. Starting search...`);

      // Run the scan
      setProgress(`🔍 Running detection scan...`);
      const scanResult = await scanner.scanContent(content);
      const { violations, visibilityData } = scanResult;

      setProgress(`✅ Scan complete! Found ${violations.length} violations. Saving to database...`);

      // Save violations to database
      let savedCount = 0;
      const violationIds: any[] = [];
      for (const violation of violations) {
        savedCount++;
        setProgress(`💾 Saving violation ${savedCount}/${violations.length}: ${violation.domain}`);
        try {
          await updateScanProgress({
            scanId,
            currentPhase: 'finalizing',
            progressText: `Saving violations ${savedCount}/${violations.length}`,
            progressCurrent: savedCount,
            progressTotal: violations.length,
          });
        } catch {}
        const violationId = await createViolation({
          scanId,
          url: violation.url,
          domain: violation.domain,
          confidenceScore: violation.score,
          signals: violation.signals,
          blurb: violation.blurb,
          screenshotUrl: violation.screenshotUrl,
          pageTitle: violation.pageTitle,
          verified: violation.verified,
          verificationMethod: violation.verificationMethod,
        });
        violationIds.push({
          id: violationId,
          domain: violation.domain,
          confidenceScore: violation.score,
          url: violation.url,
          verified: violation.verified ?? null,
          hostingInfo: null, // Will be populated later
        });
      }

      const normalizeDomain = (d: string) => (d || '').toLowerCase().replace(/^www\./, '');

      // Lookup hosting information for violations (dedupe per domain)
      if (violationIds.length > 0) {
        setProgress(`🌐 Looking up hosting information for ${violationIds.length} violations...`);
        // Initialize hosting lookup phase progress
        try {
          await updateScanProgress({
            scanId,
            currentPhase: 'hostingLookup',
            progressText: `Hosting lookup 0/${violationIds.length}`,
            progressCurrent: 0,
            progressTotal: violationIds.length,
          });
        } catch {}
        // Dedupe by normalized domain and lookup once per domain
        const groups = new Map<string, { domain: string; items: typeof violationIds }>();
        for (const v of violationIds) {
          const k = normalizeDomain(v.domain);
          if (!groups.has(k)) groups.set(k, { domain: k, items: [] as any });
          groups.get(k)!.items.push(v);
        }
        const domains = Array.from(groups.keys());
        let hostingLookupCount = 0;
        for (const k of domains) {
          const group = groups.get(k)!;
          hostingLookupCount++;
          setProgress(`🌐 Looking up hosting info ${hostingLookupCount}/${domains.length}: ${group.domain}`);
          const result = await retryWithBackoff(
            () => convex.action(api.hostingLookup.lookupHostingInfo, { domain: group.domain }),
            { maxAttempts: 3, initialDelayMs: 1000, maxDelayMs: 5000 }
          );
          const info = result.success && result.data?.lookupSuccess ? result.data : null;
          for (const v of group.items) {
            try {
              if (info) {
                await updateHostingInfo({
                  violationId: v.id,
                  hostingProvider: info.hostingProvider,
                  ipAddress: info.ipAddress,
                  ipAddresses: info.ipAddresses,
                  serverLocation: info.serverLocation,
                  asn: info.asn,
                  asnOrg: info.asnOrg,
                  abuseEmail: info.abuseEmail,
                  abuseEmails: info.abuseEmails,
                  registrarAbuseEmail: info.registrarAbuseEmail,
                  hostingAbuseEmail: info.hostingAbuseEmail,
                  registrar: info.registrar,
                  registrationDate: info.registrationDate,
                  expirationDate: info.expirationDate,
                  nameservers: info.nameservers,
                  whoisPrivacy: info.whoisPrivacy,
                  hostingLookupDate: Date.now(),
                  hostingLookupSuccess: true,
                  hostingCached: info.fromCache === true,
                });
                v.hostingInfo = info;
              } else {
                await updateHostingInfo({
                  violationId: v.id,
                  hostingLookupDate: Date.now(),
                  hostingLookupSuccess: false,
                  hostingLookupError: 'Lookup failed or unavailable',
                });
              }
            } catch {}
          }
          try {
            await updateScanProgress({
              scanId,
              currentPhase: 'hostingLookup',
              progressText: `Hosting lookup ${hostingLookupCount}/${domains.length}: ${group.domain}`,
              progressCurrent: hostingLookupCount,
              progressTotal: domains.length,
            });
          } catch {}
          if (hostingLookupCount < domains.length) await new Promise(r => setTimeout(r, 1500));
        }

        setProgress(`✅ Hosting information lookup complete for ${violationIds.length} violations`);
        try {
          await updateScanProgress({
            scanId,
            currentPhase: 'hostingLookup',
            progressText: `Hosting lookup ${violationIds.length}/${violationIds.length}`,
            progressCurrent: violationIds.length,
            progressTotal: violationIds.length,
          });
        } catch {}

        // Regional reachability checks (SA/AE/EG proxies)
        const reachabilityGroups = new Map<string, { domain: string; url: string | null; items: typeof violationIds }>();
        for (const v of violationIds) {
          const key = normalizeDomain(v.domain);
          if (!reachabilityGroups.has(key)) {
            reachabilityGroups.set(key, { domain: key, url: v.url || null, items: [] as any });
          }
          const group = reachabilityGroups.get(key)!;
          if (!group.url && v.url) {
            group.url = v.url;
          }
          group.items.push(v);
        }

        const reachabilityList = Array.from(reachabilityGroups.values());
        if (reachabilityList.length > 0) {
          setProgress(`🌍 Checking regional reachability (SA/AE/EG)...`);
          try {
            await updateScanProgress({
              scanId,
              currentPhase: 'regionalReachability',
              progressText: `Regional reachability 0/${reachabilityList.length}`,
              progressCurrent: 0,
              progressTotal: reachabilityList.length,
            });
          } catch {}

          let reachCount = 0;
          for (const group of reachabilityList) {
            reachCount++;
            const primaryUrl = group.items.find(item => item.verified)?.url || group.url || `https://${group.domain}`;
            const targetUrl = /^https?:\/\//i.test(primaryUrl || '') ? (primaryUrl as string) : `https://${group.domain}`;

            setProgress(`🌍 Queueing regional reachability ${reachCount}/${reachabilityList.length}: ${group.domain}`);
            try {
              await updateScanProgress({
                scanId,
                currentPhase: 'regionalReachability',
                progressText: `Regional reachability ${reachCount}/${reachabilityList.length}: ${group.domain}`,
                progressCurrent: reachCount,
                progressTotal: reachabilityList.length,
              });
            } catch {}

            const attempt = await retryWithBackoff(
              () => convex.action(api.reachability.requestRegionalAccessCheck, {
                violationIds: group.items.map((item: any) => item.id),
                domain: group.domain,
                url: targetUrl,
                source: 'manual',
              }),
              { maxAttempts: 2, initialDelayMs: 1500, maxDelayMs: 6000 }
            );

            if (!attempt.success || !(attempt.data as any)?.success) {
              const errorMsg = attempt.error ?? (attempt.data as any)?.error;
              // Skip logging for expected throttling (cooldown/rate limit)
              if (errorMsg && !errorMsg.toLowerCase().includes('cooldown') && !errorMsg.toLowerCase().includes('rate limit')) {
                console.warn('[Scanner] Regional reachability enqueue failed for', group.domain, errorMsg);
              }
            }

            if (reachCount < reachabilityList.length) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }

          try {
            await updateScanProgress({
              scanId,
              currentPhase: 'regionalReachability',
              progressText: `Regional reachability ${reachabilityList.length}/${reachabilityList.length}`,
              progressCurrent: reachabilityList.length,
              progressTotal: reachabilityList.length,
            });
          } catch {}

          setProgress(`✅ Regional reachability checks complete`);
        }

        // Update domain analytics for all violations
        setProgress(`📈 Updating domain analytics...`);
        let da = 0;
        for (const violation of violationIds) {
          try {
            await updateDomainAnalytics({
              violationId: violation.id,
              domain: violation.domain,
              contentTitle: content.title,
              confidenceScore: violation.confidenceScore,
              hostingProvider: violation.hostingInfo?.hostingProvider,
              ipAddress: violation.hostingInfo?.ipAddress,
              serverLocation: violation.hostingInfo?.serverLocation,
              registrar: violation.hostingInfo?.registrar,
              registrationDate: violation.hostingInfo?.registrationDate,
              whoisPrivacy: violation.hostingInfo?.whoisPrivacy,
              registrarAbuseEmail: violation.hostingInfo?.registrarAbuseEmail,
            });
            da++;
            try {
              await updateScanProgress({
                scanId,
                currentPhase: 'finalizing',
                progressText: `Domain analytics ${da}/${violationIds.length}`,
                progressCurrent: da,
                progressTotal: violationIds.length,
              });
            } catch {}
          } catch (error: any) {
            console.error(`Failed to update domain analytics for ${violation.domain}:`, error);
          }
        }
        setProgress(`✅ Domain analytics updated`);
      }

      // Save visibility data
      if (visibilityData && visibilityData.length > 0) {
        setProgress(`📊 Saving visibility data...`);

        for (const vd of visibilityData) {
          await createVisibilityData({
            scanId,
            query: vd.query,
            searchEngine: vd.searchEngine,
            totalResults: vd.totalResults,
            osnRank: vd.osnRank,
            piratesBeforeOsn: vd.piratesBeforeOsn,
            totalPirates: vd.totalPirates,
            visibilityScore: vd.visibilityScore,
            alpha: vd.alpha,
            beta: vd.beta,
            osnUrls: vd.osnUrls,
            pirateUrls: vd.pirateUrls,
          });
        }

        // Calculate aggregate visibility score
        const avgVisibilityScore = visibilityData.reduce((sum, v) => sum + v.visibilityScore, 0) / visibilityData.length;
        const firstOsnRank = visibilityData.find(v => v.osnRank !== null)?.osnRank || null;
        // If OSN+ is not found in ANY query, all violations are "before" OSN+
        // Otherwise, average the per-query counts
        const avgPiratesBeforeOsn = firstOsnRank === null
          ? violations.length
          : Math.round(visibilityData.reduce((sum, v) => sum + (v.piratesBeforeOsn ?? 0), 0) / visibilityData.length);

        // Update scan with visibility scores
        await updateVisibilityScores({
          scanId,
          visibilityScore: Math.round(avgVisibilityScore * 100) / 100,
          osnRank: firstOsnRank,
          piratesBeforeOsn: avgPiratesBeforeOsn,
        });

        setProgress(`✅ Visibility data saved. Score: ${avgVisibilityScore.toFixed(1)}/100`);
      }

      // Update status to complete
      setProgress(`✅ All data saved. Marking scan as complete...`);
      await updateScanStatus({
        scanId,
        status: 'complete',
      });

      // Clear progress on completion
      await updateScanProgress({
        scanId,
        currentPhase: undefined,
        progressText: undefined,
        progressCurrent: undefined,
        progressTotal: undefined
      });

      setProgress(`🎉 Scan complete! ${violations.length} violations found for ${content.title}`);
      setProgressCount(null);
      return scanResult;

    } catch (error: any) {
      console.error('Scan error:', error);

      await updateScanStatus({
        scanId,
        status: 'error',
        errorMessage: error.message,
      });

      setProgress(`Scan failed: ${error.message}`);
      setProgressCount(null);
      throw error;
    } finally {
      setIsScanning(false);
    }
  }, [convex, createViolation, updateScanStatus, updateScanProgress, updateVisibilityScores, createVisibilityData, updateHostingInfo, updateDomainAnalytics, whitelist, visibilityConfig]);

  return {
    runScan,
    isScanning,
    progress,
    progressCount,
  };
}
